"use client";

import { GrowthBookProvider } from "@growthbook/growthbook-react";
import React, { useEffect, useState, useSyncExternalStore } from "react";

import { createGrowthBookClient } from "./growthbook.client";
import { resolveGrowthBookClientKey } from "./growthbook.config";
import { getOrCreateGrowthBookClient, subscribeToGrowthBookClient } from "./growthbook.runtime";
import type { FeatureFlagAttributes } from "./types";

/**
 * The browser's shared client: `growthbook.runtime` owns it and the
 * growthbook sink starts and stops it. The same function serves as the
 * server snapshot, where the runtime holds nothing and answers `null` — the
 * server pass uses the per-request client constructed below.
 */
const getSharedGrowthBookClient = () => getOrCreateGrowthBookClient();

/**
 * Whether this render owns its client: only on the server. Module state must
 * never be shared across requests, so the runtime's slot is browser-only and
 * the server pass gets a throwaway instance.
 */
const rendersOwnClient = (): boolean => typeof window === "undefined";

type FeatureFlagProviderProps = {
  readonly children: React.ReactNode;
  /**
   * Server-fetched features from GrowthBook CDN
   * If not provided, features will be loaded on the client
   */
  readonly features?: Record<string, unknown> | null;
  /**
   * User context/attributes for feature flag targeting
   */
  readonly attributes?: FeatureFlagAttributes;
};

/**
 * Feature Flag Provider using GrowthBook
 * This is a client component that wraps the app with GrowthBookProvider
 * It should be used in the root layout to provide feature flags to the entire app
 */
export function FeatureFlagProvider({
  children,
  features,
  attributes = {},
}: FeatureFlagProviderProps): React.ReactElement {
  // Create the GrowthBook instance once, and seed the server-fetched features into it *here* rather
  // than in an effect below. React runs child effects before parent ones, so features set in this
  // component's effect land after every consumer has already evaluated — and because the context
  // value never changes identity, `useGrowthBook()` consumers are not re-rendered when it does.
  // The result was a subtree frozen on whatever each flag evaluated to with no features loaded:
  // its default. Assessment experiments read as control for everyone, and recorded that as a real
  // assignment. Seeding synchronously means the very first child render already sees the rules.
  const [ownedGb] = useState(() => {
    // In the browser the client is the runtime's, read below; constructing
    // one here would be the double bucketing the runtime exists to prevent.
    if (!rendersOwnClient()) {
      return null;
    }

    const instance = createGrowthBookClient();
    if (features) {
      instance.setFeatures(features as unknown as Record<string, any>);
    }
    return instance;
  });
  const sharedGb = useSyncExternalStore(
    subscribeToGrowthBookClient,
    getSharedGrowthBookClient,
    getSharedGrowthBookClient,
  );
  const gb = ownedGb ?? sharedGb;
  if (gb === null) {
    // Impossible by construction: exactly one of the two branches above
    // yields a client for a given environment.
    throw new Error("FeatureFlagProvider has no GrowthBook client for this build");
  }
  // The shared client needs the same synchronous seeding as the owned one
  // gets in its initializer, for the same reason (see above) — and again for
  // each fresh instance the runtime hands out after a stop, which is why it
  // is keyed on readiness rather than done once.
  if (ownedGb === null && features && !gb.ready) {
    gb.setFeatures(features as unknown as Record<string, any>);
  }

  // Merge, do not replace: child hooks (e.g. assessment experiments) also call
  // setAttributes with session-scoped keys like assessmentId. React runs child
  // effects before parent effects, so a full replace here would wipe those keys
  // and GrowthBook would skip the experiment (everyone gets the default/control).
  useEffect(() => {
    gb.setAttributes({ ...gb.getAttributes(), ...attributes });
  }, [gb, attributes]);

  // Set features if provided from server, otherwise load from client
  useEffect(() => {
    if (features) {
      // GrowthBook expects a specific features format, but we're passing the CDN response
      // Using unknown then the expected type to maintain type safety
      gb.setFeatures(features as unknown as Record<string, any>);
      return;
    }

    if (!resolveGrowthBookClientKey()) {
      // No client key: there is nothing to load, but consumers gate on `gb.ready` to know whether a
      // flag value is trustworthy. Publish an empty feature set so readiness still resolves (every
      // flag falls back to its default) instead of leaving `ready` false forever.
      gb.setFeatures({});
      return;
    }

    // If the client-side load settles without ever producing features (CDN outage), publish the
    // empty set: every flag falls back to its default and consumers that gate UI on readiness
    // (the pre-scan question list) still render, instead of blanking the funnel until the CDN
    // recovers. Nothing is recorded as an assignment on this path — an empty map evaluates no
    // experiments.
    void gb.loadFeatures().finally(() => {
      if (!gb.ready) {
        gb.setFeatures({});
      }
    });
  }, [gb, features]);

  useEffect(() => {
    // The shared client is stopped by the growthbook sink when the engine
    // stops, never by this tree unmounting; only a server-owned throwaway
    // instance is destroyed here.
    if (ownedGb === null) {
      return;
    }

    return () => {
      gb.destroy();
    };
  }, [gb, ownedGb]);

  return <GrowthBookProvider growthbook={gb}>{children}</GrowthBookProvider>;
}
